/**
 * The page (docs/PLAN_V4_GOP_ANH_THE_CHINH_ANH_2026-10-29.md, GĐ1): one top bar, account and sign-in, tokens, one
 * history and one consent box for two modes — "Ảnh thẻ" (studio/IdPhotoMode.tsx: spec, QC, "đạt chuẩn") and "Ảnh
 * chuyên nghiệp" (editor/EditorApp.tsx: the v4 editor, its own chunk). `/` opens the mode used last, `/chinh-anh` the
 * editor. A mode once opened stays mounted (hidden) so its photo and choices are there when the user comes back; the
 * photo picked in one mode opens in the other too.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ComponentType } from "react";
import { ApiError, type Api, type Catalog, type Job, type Me, type Status } from "./api";
import { AccountMenu } from "./components/AccountMenu";
import { CoinBadge, RenderStatus } from "./components/CoinBadge";
import { ContactButtons } from "./components/ContactButtons";
import { LoginDialog } from "./components/LoginDialog";
import { ReferralDialog } from "./components/ReferralDialog";
import type { AuthState } from "./components/RenderBar";
import { EDIT_PATH, TopNav, pathOf } from "./components/TopNav";
import { config } from "./config";
import type { EditCatalog } from "./editor/types";
import { TIER_LABELS, errorMessage, tierLine } from "./messages";
import { readProfile, saveProfile, type Profile } from "./profile";
import { STATUS_POLL_MS } from "./queues";
import { readLocal, writeLocal } from "./storage";
import { IdPhotoMode } from "./studio/IdPhotoMode";
import { isEditJob, isSharpenJob, mergeJobs } from "./studio/jobs";
import { specParam } from "./studio/specLink";
import type { LoginRequest, Mode, ModeProps, OpenRequest, SharedPhoto, Studio } from "./studio/types";
import { useSharpen } from "./studio/useSharpen";
import { codeOf } from "./studio/util";

const CONSENT_KEY = "idphoto.consent.v2";                     // "v1" (ID photos) and "edit.v1": asked again once
const PROVIDER_CONSENT_KEY = "idphoto.consent.providers.v1"; // {"google": true}: photos may go to that provider
const MODE_KEY = "idphoto.mode.v1";

const LazyProMode = lazy(() => import("./editor/EditorApp").then((m) => ({ default: m.ProMode })));

function readProviderConsents(): Record<string, boolean> {
  try {
    const v: unknown = JSON.parse(readLocal(PROVIDER_CONSENT_KEY) || "{}");
    return v && typeof v === "object" ? (v as Record<string, boolean>) : {};
  } catch {
    return {}; // unreadable: ask again
  }
}

/** The mode an address shows: /chinh-anh = the editor, anything else = ID photos. */
const modeOfPath = (): Mode => (window.location.pathname.replace(/\/+$/, "") === EDIT_PATH ? "pro" : "idphoto");

/** The mode to open: /chinh-anh = the editor; / = the mode used last in this browser (`/?spec=`: ID photos). */
export function modeFromLocation(): Mode {
  if (modeOfPath() === "pro") return "pro";
  if (specParam() !== null) return "idphoto";        // "Làm ảnh ngay" of a /quy-cach guide page
  return readLocal(MODE_KEY) === "pro" ? "pro" : "idphoto";
}

interface Props {
  api: Api;
  pollMs?: number;
  devUser?: string | null;
  /** the mode to open (default: from the address) */
  initialMode?: Mode;
  /** "Ảnh chuyên nghiệp" (default: its own chunk, loaded when first shown) */
  ProMode?: ComponentType<ModeProps>;
}

export function App({ api, pollMs = config.pollMs, devUser, initialMode, ProMode = LazyProMode }: Props) {
  const [mode, setMode] = useState<Mode>(() => initialMode ?? modeFromLocation());
  const [visited, setVisited] = useState<Record<Mode, boolean>>(() => ({ idphoto: mode === "idphoto", pro: mode === "pro" }));
  const [me, setMe] = useState<Me | null>(null);
  const [auth, setAuth] = useState<AuthState>("loading");
  const [status, setStatus] = useState<Status | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [editCatalog, setEditCatalog] = useState<EditCatalog | null>(null);
  const [editCatalogError, setEditCatalogError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(readProfile); // top-right corner; this browser only
  const [devName, setDevName] = useState(devUser ?? null);
  const [login, setLogin] = useState<LoginRequest | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [consent, setConsentState] = useState(readLocal(CONSENT_KEY) === "yes");
  const [providerConsents, setProviderConsents] = useState<Record<string, boolean>>(readProviderConsents);
  const [shared, setShared] = useState<SharedPhoto | null>(null);
  const [openRequest, setOpenRequest] = useState<(OpenRequest & { mode: Mode }) | null>(null);
  const [referral, setReferral] = useState(false);              // "Giới thiệu bạn bè" dialog
  const lists = useRef<{ id: Job[]; edit: Job[] }>({ id: [], edit: [] }); // the last good answer of each list
  const seq = useRef(0);
  const openSeq = useRef(0);

  const forgetProfile = useCallback(() => {
    setProfile(null);
    saveProfile(null);
  }, []);

  const refreshMe = useCallback(async (): Promise<Me | null> => {
    try {
      const m = await api.me();
      setMe(m);
      setAuth("in");
      return m;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setMe(null);
        setAuth("guest");
        forgetProfile();
      } else {
        setLoadError(codeOf(e));
        setAuth((a) => (a === "loading" ? "guest" : a));
      }
      return null;
    }
  }, [api, forgetProfile]);

  const refreshJobs = useCallback(async () => {
    // the page works without either list; one failing keeps its last answer
    const [ids, edits] = await Promise.all([
      Promise.resolve().then(() => api.jobs()).catch((e) => { console.warn("history unavailable", e); return null; }),
      Promise.resolve().then(() => api.editJobs()).catch((e) => { console.warn("edit history unavailable", e); return null; }),
    ]);
    if (ids) lists.current.id = ids;
    if (edits) lists.current.edit = edits;
    setJobs(mergeJobs(lists.current.id, lists.current.edit));
  }, [api]);

  useEffect(() => {
    if (api.maybeSignedIn()) void refreshMe();
    else {
      setAuth("guest");
      forgetProfile(); // the session is gone: so is who was signed in
    }
    // each mode needs its own catalogue; a mode shows the error of its catalogue only
    Promise.resolve().then(() => api.specs()).then(setCatalog).catch((e) => setCatalogError(codeOf(e)));
    Promise.resolve().then(() => api.editCatalog()).then(setEditCatalog).catch((e) => setEditCatalogError(codeOf(e)));
  }, [api, refreshMe, forgetProfile]);

  useEffect(() => {
    if (auth === "in") void refreshJobs();
  }, [auth, refreshJobs]);

  useEffect(() => {          // contact buttons + which queues a render machine serves: again every 30 s while visible
    let stop = false;
    const load = () => {
      if (document.visibilityState === "hidden") return;
      api.status().then((s) => { if (!stop) setStatus(s); }).catch((e) => console.warn("status unavailable", e));
    };
    load();
    const timer = window.setInterval(load, STATUS_POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };   // back on the tab: at once
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stop = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api]);

  useEffect(() => {                                   // back / forward between / and /chinh-anh
    const onPop = () => show(modeOfPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function show(next: Mode) {
    setMode(next);
    setVisited((v) => (v[next] ? v : { ...v, [next]: true }));
    writeLocal(MODE_KEY, next);
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    show(next);
    window.history.pushState(null, "", `${pathOf(next)}${window.location.search}`);
  }

  function signedOut() {
    setMe(null);
    setAuth("guest");
    forgetProfile();
  }

  function onSignedIn(m: Me, p: Profile | null, name?: string) {
    const waiting = login;
    setMe(m);
    setAuth("in");
    setLogin(null);
    setProfile(p);
    saveProfile(p);
    if (name) setDevName(name);
    waiting?.onSignedIn?.(m);
  }

  function closeLogin() {
    login?.onCancel?.();
    setLogin(null);
  }

  async function logout() {
    try {
      await api.logout();
    } catch (e) {
      console.warn("logout request failed; signing out here anyway", e); // the session cookie may already be gone
    }
    if (config.authMode === "dev") writeLocal("idphoto.devUser", ""); // a reload stays signed out
    signedOut();
    lists.current = { id: [], edit: [] };
    setJobs([]);
    setEpoch((n) => n + 1);
  }

  function setConsent(given: boolean) {
    setConsentState(given);
    writeLocal(CONSENT_KEY, given ? "yes" : "no");
  }

  function setProviderConsent(provider: string, given: boolean) {
    const next = { ...providerConsents, [provider]: given };
    setProviderConsents(next);
    writeLocal(PROVIDER_CONSENT_KEY, JSON.stringify(next));
  }

  function share(files: File[], opts: { keep?: boolean; sharpened?: string | null } = {}): number {
    seq.current += 1;
    setShared({ files, seq: seq.current, keep: Boolean(opts.keep), sharpened: opts.sharpened ?? null });
    return seq.current;
  }

  const sharpen = useSharpen({
    api, pollMs, me, auth, consent, editCatalog, shared, epoch, refreshMe, refreshJobs, signedOut,
    requestLogin: setLogin, share,
  });

  function openJob(job: Job) {
    if (isSharpenJob(job)) {                           // its report shows in the mode on screen
      sharpen.open(job);
      return;
    }
    const target: Mode = isEditJob(job) ? "pro" : "idphoto";
    switchMode(target);
    openSeq.current += 1;
    setOpenRequest({ job, seq: openSeq.current, mode: target });
  }

  const studio: Studio = {
    api, pollMs, me, auth, devName, refreshMe, signedOut, requestLogin: setLogin, epoch, jobs, refreshJobs, catalog,
    editCatalog, consent, setConsent, providerConsents, setProviderConsent, shared, share, sharpen, openJob, switchMode,
    status,
  };
  const props = (m: Mode): ModeProps => ({ studio, active: mode === m, open: openRequest?.mode === m ? openRequest : null });
  const banner = loadError ?? (mode === "idphoto" ? catalogError : editCatalogError);

  return (
    <>
      <div className="app" inert={login !== null || (referral && auth === "in")}>
        <header className="topbar">
          <div className="brand">
            <span className="brand-logo"><img src="/dtaa-logo.png" alt="DTAA" width={91} height={33} /></span>
            <TopNav current={mode} onMode={switchMode} />
          </div>
          <div className="topbar-right">
            <RenderStatus online={me?.render_online ?? status?.render_online} />
            {auth === "guest" && (
              <button type="button" className="btn btn-signin" onClick={() => setLogin({ forRender: false })}>Đăng nhập</button>
            )}
            {auth === "loading" && <CoinBadge me={null} />}
            {auth === "in" && (
              <>
                {me?.tier && me.tier !== "normal" && (
                  <span className={`tier-chip tier-${me.tier}`}>{TIER_LABELS[me.tier]}</span>
                )}
                <CoinBadge me={me} />
                <AccountMenu profile={profile} onLogout={() => void logout()} isAdmin={me?.is_admin}
                             tier={tierLine(me?.tier, me?.tier_until)}
                             onReferral={me?.referral_reward ? () => setReferral(true) : undefined} />
              </>
            )}
          </div>
        </header>
        {banner && <p role="alert" className="notice notice-error banner">{errorMessage(banner)}</p>}

        {visited.idphoto && <IdPhotoMode {...props("idphoto")} />}
        {visited.pro && (
          <Suspense fallback={mode === "pro" ? <div className="skeleton" aria-busy="true" aria-label="Đang tải công cụ" /> : null}>
            <ProMode {...props("pro")} />
          </Suspense>
        )}
        <ContactButtons contact={status?.contact} />
      </div>

      {login && (
        <LoginDialog api={api} cost={login.cost ?? me?.cost_per_job ?? 1} forRender={login.forRender}
                     devUser={devName ?? undefined} onSignedIn={onSignedIn} onClose={closeLogin} />
      )}
      {referral && auth === "in" && <ReferralDialog api={api} onClose={() => setReferral(false)} />}
    </>
  );
}
